<template>
  <div class="pp-price-card pa-price-public">
    <div class="status-strip">
      <div class="st">
        <span class="dot" />
        <span>{{ t('property.access.publicPriceCard.nowSelling') }}</span>
      </div>
      <div
        class="star"
        aria-hidden="true"
      >
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="currentColor"
        >
          <path d="M12 2l3 7 7 .5-5.5 4.5L18 21l-6-3.8L6 21l1.5-7L2 9.5 9 9z" />
        </svg>
      </div>
    </div>

    <div class="body">
      <h2 class="big-price">
        <template v-if="priceMin > 0">
          <span class="from">{{ t('property.heroCard.from') }}</span>
          <span class="amount tabular-nums">{{ formatPrice(priceMin) }}</span>
          <span class="cur">{{ currency }}</span>
        </template>
        <template v-else>
          <span class="amount">{{ property.title.en }}</span>
        </template>
      </h2>

      <div
        v-if="unitBands.length"
        class="unit-chips"
      >
        <span
          v-for="band in unitBands"
          :key="band.label"
          class="c"
          >{{ bandLabel(band) }}</span
        >
      </div>

      <hr />

      <div class="rowpair">
        <span class="lbl">{{ t('property.heroCard.developer') }}</span>
        <span class="val">{{ developerLabel }}</span>
      </div>
      <div class="rowpair locked">
        <span class="lbl">{{ t('property.access.publicPriceCard.pricePerSqft') }}</span>
        <span class="val pa-blur">{{ formatLockedAed(property.pricePerSqft, 'AED 2,180') }}</span>
      </div>
      <div class="rowpair locked">
        <span class="lbl">{{ t('property.access.publicPriceCard.serviceCharge') }}</span>
        <span class="val pa-blur">{{ formatLockedAed(property.serviceCharge, 'AED 18.50 / sqft') }}</span>
      </div>
      <div class="rowpair locked">
        <span class="lbl">{{ t('property.v2Overview.handover') }}</span>
        <span class="val pa-blur">{{ property.handoverYear ?? 'Q4 2027' }}</span>
      </div>
      <div class="rowpair locked">
        <span class="lbl">{{ t('property.shell.tabs.paymentPlan') }}</span>
        <span class="val pa-blur">{{ property.paymentPlan ?? '60 / 40' }}</span>
      </div>

      <div class="pa-price-locknote">
        <svg
          width="11"
          height="11"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <rect
            x="4"
            y="11"
            width="16"
            height="10"
            rx="2"
          />
          <path d="M8 11V8a4 4 0 1 1 8 0v3" />
        </svg>
        <i18n-t
          keypath="property.access.publicPriceCard.lockNote"
          tag="span"
        >
          <template #bold
            ><b>{{ t('property.access.publicPriceCard.lockNoteBold') }}</b></template
          >
        </i18n-t>
      </div>

      <div class="ctas">
        <button
          type="button"
          class="cta-gold"
          @click="emit('enquire-click')"
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <rect
              x="4"
              y="11"
              width="16"
              height="10"
              rx="2"
            />
            <path d="M8 11V8a4 4 0 1 1 8 0v3" />
          </svg>
          <span
            >{{ t('property.access.publicPriceCard.enquireArrow') }}
            <span
              class="inline-block rtl:-scale-x-100"
              aria-hidden="true"
              >→</span
            ></span
          >
        </button>
        <button
          type="button"
          class="cta-ghost"
          @click="emit('save-click')"
        >
          {{ t('property.titleRow.saveToFavorites') }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import type { UnitBand, UnitBandKey } from '~/utils/fuzzyUnitBands';

const { t } = useI18n();

const UNIT_BAND_LABEL_KEYS: Record<UnitBandKey, string> = {
  studio: 'property.access.publicPriceCard.bands.studio',
  '1br': 'property.access.publicPriceCard.bands.oneBr',
  '2br': 'property.access.publicPriceCard.bands.twoBr',
  '3br': 'property.access.publicPriceCard.bands.threeBr',
  '4br+': 'property.access.publicPriceCard.bands.fourBrPlus',
};
const bandLabel = (band: UnitBand) =>
  t('property.access.publicPriceCard.bandFrom', { unit: t(UNIT_BAND_LABEL_KEYS[band.key]), price: band.priceLabel });
const { formatNumber } = useFormat();

const props = defineProps<{
  property: {
    title: { en: string };
    developer?: { en?: string } | null;
    price: { min: string | number; currency?: string };
    pricePerSqft?: string | number | null;
    serviceCharge?: string | number | null;
    handoverYear?: string | number | null;
    paymentPlan?: string | null;
    apartments?: unknown[];
  };
  unitBands: UnitBand[];
}>();

const emit = defineEmits<{
  (e: 'enquire-click'): void;
  (e: 'save-click'): void;
}>();

const priceMin = computed(() => {
  const raw = props.property.price?.min;
  const n = typeof raw === 'number' ? raw : Number(String(raw ?? '').replace(/[^\d.]/g, ''));
  return Number.isFinite(n) ? n : 0;
});

const currency = computed(() => props.property.price?.currency ?? 'AED');

const developerLabel = computed(() => props.property.developer?.en?.trim() || '—');

const formatPrice = (n: number) => {
  if (!Number.isFinite(n) || n <= 0) return '—';
  return formatNumber(n, { style: 'decimal', maximumFractionDigits: 0 });
};

// Fallback strings ('AED 2,180', '60 / 40'...) are illustrative locked-preview
// placeholders shown behind a blur, not real translatable prose — kept as-is.
const formatLockedAed = (val: string | number | null | undefined, fallback: string) => {
  if (val == null || val === '') return fallback;
  if (typeof val === 'number') return `AED ${formatNumber(val)}`;
  return String(val);
};
</script>

<style scoped lang="scss">
.pp-price-card {
  background: #fff0da;
  border-radius: 16px;
  overflow: hidden;
  border: 1px solid rgba(255, 182, 72, 0.4);

  &.pa-price-public {
    border-color: #ffb648;
  }

  .status-strip {
    height: 56px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding-block: 0;
    padding-inline: 20px 22px;
    background: white;
    border-bottom: 1px solid rgba(255, 182, 72, 0.4);

    .st {
      display: flex;
      align-items: center;
      gap: 8px;
      font-size: 13px;
      font-weight: 600;
      color: #0d0f15;
    }

    .dot {
      width: 8px;
      height: 8px;
      border-radius: 50%;
      background: #22c55e;
    }

    .star {
      width: 40px;
      height: 40px;
      border-radius: 10px;
      background: #0d0f15;
      color: #ffb648;
      display: inline-flex;
      align-items: center;
      justify-content: center;
    }
  }

  .body {
    padding: 22px;
  }

  .big-price {
    font-family: 'Fraunces', 'Times New Roman', serif;
    font-weight: 600;
    font-size: 32px;
    letter-spacing: -0.02em;
    line-height: 1.1;
    color: #0d0f15;
    margin: 0 0 12px;
    font-variant-numeric: tabular-nums;

    .from {
      font-family: 'Manrope', sans-serif;
      font-size: 14px;
      font-weight: 500;
      color: rgba(13, 15, 21, 0.62);
      margin-inline-end: 6px;
    }

    .cur {
      font-family: 'JetBrains Mono', ui-monospace, monospace;
      font-size: 14px;
      color: rgba(13, 15, 21, 0.62);
      margin-inline-start: 6px;
      font-weight: 500;
    }
  }

  .unit-chips {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-bottom: 14px;

    .c {
      padding: 5px 10px;
      border-radius: 7px;
      background: white;
      border: 1px solid rgba(13, 15, 21, 0.08);
      font-size: 12px;
      font-weight: 600;
      color: #0d0f15;
    }
  }

  hr {
    border: none;
    border-top: 1px solid rgba(255, 182, 72, 0.45);
    margin: 14px 0;
  }

  .rowpair {
    display: flex;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 12px;

    .lbl {
      font-size: 12.5px;
      color: rgba(13, 15, 21, 0.62);
    }

    .val {
      font-size: 14px;
      font-weight: 700;
      color: #0d0f15;
      text-align: end;
    }
  }

  .pa-blur {
    filter: blur(4px);
    user-select: none;
    letter-spacing: 0.04em;
  }

  .pa-price-locknote {
    margin-top: 12px;
    padding: 8px 10px;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    background: rgba(13, 15, 21, 0.05);
    border-radius: 8px;
    font-size: 11.5px;
    color: rgba(13, 15, 21, 0.7);

    svg {
      color: #b8821a;
      flex-shrink: 0;
    }

    b {
      color: #0d0f15;
      font-weight: 700;
    }
  }

  .ctas {
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-top: 12px;
  }

  .cta-gold {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    height: 48px;
    padding: 0 16px;
    border-radius: 12px;
    background: #ffb648;
    color: #0d0f15;
    font-size: 14px;
    font-weight: 700;
    transition: background 0.15s ease;
    border: none;
    cursor: pointer;

    &:hover {
      background: #eda436;
    }
  }

  .cta-ghost {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    height: 48px;
    padding: 0 16px;
    border-radius: 12px;
    background: transparent;
    color: #0d0f15;
    border: 1px solid rgba(13, 15, 21, 0.25);
    font-size: 14px;
    font-weight: 700;
    transition: background 0.15s ease;
    cursor: pointer;

    &:hover {
      background: rgba(255, 255, 255, 0.6);
    }
  }
}
</style>
